import { FormattedText } from "@tm/static";
import { Fragment, useEffect, useMemo } from "react";
import { useCancelPolicyStep } from "../CancelPolicyContext";
import type {
  CancelPolicyBreakupItem,
  CancelPolicyPaymentGroup,
  CancelPolicyRefundBreakupData,
} from "../types";
import {
  BreakupRow,
  LabelCell,
  PaymentGroup,
  PaymentSection,
  refundAmountStyle,
  refundBreakupLabelStyle,
  refundBreakupValueStyle,
  RefundRow,
  refundSummaryStyle,
  StepCard,
  StepCardDivider,
  SummaryRow,
} from "./CancelPolicyStepCards.style";

/**
 * Splits payment groups into visually-divided sections.
 * A "summary" row (a group without `breakupList`) closes the current section;
 * the next group then starts a new section preceded by a divider.
 */
const splitPaymentSections = (
  paymentGroups?: CancelPolicyPaymentGroup[]
): CancelPolicyPaymentGroup[][] => {
  if (!paymentGroups?.length) {
    return [];
  }

  const sections: CancelPolicyPaymentGroup[][] = [];
  let current: CancelPolicyPaymentGroup[] = [];

  paymentGroups.forEach((group, index) => {
    current.push(group);
    const isSummary = !group.breakupList?.length;
    const isLast = index === paymentGroups.length - 1;
    if (isSummary && !isLast) {
      sections.push(current);
      current = [];
    }
  });

  if (current.length) {
    sections.push(current);
  }
  return sections;
};

const Summary = ({ item }: { item: CancelPolicyBreakupItem }) => (
  <SummaryRow>
    <LabelCell>
      <FormattedText value={item.label} baseStyle={refundSummaryStyle} />
    </LabelCell>
    <FormattedText value={item.value} baseStyle={refundSummaryStyle} />
  </SummaryRow>
);

const Breakup = ({ item }: { item: CancelPolicyBreakupItem }) => (
  <BreakupRow>
    <LabelCell>
      <FormattedText value={item.label} baseStyle={refundBreakupLabelStyle} />
    </LabelCell>
    <FormattedText value={item.value} baseStyle={refundBreakupValueStyle} />
  </BreakupRow>
);

export const CancelPolicyRefundBreakup = ({
  paymentGroups,
  refundAmount,
}: CancelPolicyRefundBreakupData) => {
  const { onStepStateChange } = useCancelPolicyStep();
  const paymentSections = useMemo(
    () => splitPaymentSections(paymentGroups),
    [paymentGroups]
  );
  const hasRefund = Boolean(refundAmount?.label || refundAmount?.value);

  useEffect(() => {
    onStepStateChange?.({ isValid: true });
  }, [onStepStateChange]);

  return (
    <StepCard>
      {paymentSections.map((groups, sectionIndex) => (
        <Fragment key={`section-${sectionIndex}`}>
          {sectionIndex > 0 ? <StepCardDivider /> : null}
          <PaymentSection>
            {groups.map((group, groupIndex) => (
              <PaymentGroup key={`group-${sectionIndex}-${groupIndex}`}>
                <Summary item={group} />
                {group.breakupList?.map((item, itemIndex) => (
                  <Breakup key={`breakup-${itemIndex}`} item={item} />
                ))}
              </PaymentGroup>
            ))}
          </PaymentSection>
        </Fragment>
      ))}

      {hasRefund ? (
        <>
          <StepCardDivider />
          <RefundRow>
            <LabelCell>
              <FormattedText
                value={refundAmount?.label}
                baseStyle={refundAmountStyle}
              />
            </LabelCell>
            <FormattedText
              value={refundAmount?.value}
              baseStyle={refundAmountStyle}
            />
          </RefundRow>
        </>
      ) : null}
    </StepCard>
  );
};

export default CancelPolicyRefundBreakup;
